/**
 * Macro recording controls component.
 * Displays recording controls in the app header.
 * Collapses to a simple "Record" button when not recording.
 * Expands to show pulsing indicator and control buttons when recording.
 */

import { isRecording, startRecording, stopRecording, clearActions, getRecordedActions, subscribeMacro } from '../../state/macro.js';
import { initRecorder, cleanupRecorder } from '../../macro/recorder.js';

/**
 * Render the macro controls based on current recording state.
 * @param {HTMLElement} container - Container element to render into
 */
export function renderMacroControls(container) {
  const recording = isRecording();
  const actions = getRecordedActions();

  if (!recording) {
    // Collapsed state: simple "Record" button
    container.innerHTML = `
      <button id="macro-start-btn" class="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke-width="2"></circle>
          <circle cx="12" cy="12" r="4" fill="currentColor"></circle>
        </svg>
        Record
      </button>
    `;
  } else {
    // Expanded state: pulsing indicator, action count, and control buttons
    container.innerHTML = `
      <div class="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200">
        <!-- Pulsing red dot indicator -->
        <span class="relative flex h-3 w-3">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
        </span>
        <span class="text-sm text-red-700 font-medium">Recording</span>
        <span class="text-xs text-red-500" id="macro-count">(${actions.length} action${actions.length === 1 ? '' : 's'})</span>

        <div class="flex items-center gap-1 ml-2 border-l border-red-200 pl-2">
          <button id="macro-preview-btn" class="px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded transition-colors">Preview</button>
          <button id="macro-stop-btn" class="px-2 py-1 text-xs text-red-600 hover:bg-red-100 rounded transition-colors">Stop</button>
          <button id="macro-clear-btn" class="px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded transition-colors">Clear</button>
        </div>
      </div>
    `;
  }
}

/**
 * Initialize macro controls with event handlers and state subscription.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function that unsubscribes and cleans up
 */
export function initMacroControls(container) {
  // Track if recorder has been initialized
  let recorderInitialized = false;

  // Initial render
  renderMacroControls(container);

  /**
   * Handle click events via delegation.
   * @param {Event} event - Click event
   */
  function handleClick(event) {
    const target = event.target.closest('button');
    if (!target) return;

    const id = target.id;

    switch (id) {
      case 'macro-start-btn':
        // Initialize recorder if not already done
        if (!recorderInitialized) {
          initRecorder();
          recorderInitialized = true;
        }
        startRecording();
        break;

      case 'macro-stop-btn':
        // Stop recording but keep recorder active for potential playback
        stopRecording();
        break;

      case 'macro-clear-btn':
        clearActions();
        break;

      case 'macro-preview-btn':
        // Dispatch custom event on document for preview modal to handle
        document.dispatchEvent(new CustomEvent('macro:preview', {
          detail: { actions: getRecordedActions() }
        }));
        break;
    }
  }

  // Attach delegated click handler
  container.addEventListener('click', handleClick);

  // Subscribe to macro state changes for re-renders
  const unsubscribe = subscribeMacro(() => {
    renderMacroControls(container);
  });

  // Return cleanup function
  return function cleanup() {
    unsubscribe();
    container.removeEventListener('click', handleClick);
    // Clean up recorder if it was initialized
    if (recorderInitialized) {
      cleanupRecorder();
      recorderInitialized = false;
    }
  };
}
